import { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { getTeams, createTeam, deleteTeam, getGroups } from '../../lib/api';
import { toast } from 'sonner';

export const AdminSquadrePage = () => {
  const [teams, setTeams] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTeamName, setNewTeamName] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchData = async () => {
    try {
      const [teamsRes, groupsRes] = await Promise.all([
        getTeams(),
        getGroups()
      ]);
      setTeams(teamsRes.data);
      setGroups(groupsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle squadre');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;
    
    setAdding(true);
    try {
      await createTeam(newTeamName.trim());
      toast.success('Squadra aggiunta');
      setNewTeamName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta della squadra');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    try {
      await deleteTeam(teamId);
      toast.success('Squadra eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getGroupName = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-squadre">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">GESTIONE SQUADRE</h1>
          <p className="text-slate-400">
            {teams.length}/12 squadre iscritte
          </p>
        </div>

        {/* Add Team Form */}
        <Card className="bg-slate-900 border-slate-800 mb-8">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Aggiungi Squadra
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddTeam} className="flex gap-3">
              <Input
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="Nome squadra"
                className="bg-slate-950 border-slate-800"
                disabled={teams.length >= 16}
                data-testid="new-team-input"
              />
              <Button 
                type="submit" 
                disabled={adding || teams.length >= 16}
                className="bg-primary text-slate-950 hover:bg-primary/90"
                data-testid="add-team-btn"
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aggiungi'}
              </Button>
            </form>
            {teams.length >= 16 && (
              <p className="mt-2 text-sm text-gold">Massimo 12 squadre raggiunto</p>
            )}
          </CardContent>
        </Card>

        {/* Teams List */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Squadre Iscritte ({teams.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {teams.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Nome Squadra</TableHead>
                    <TableHead className="w-24 text-center">Girone</TableHead>
                    <TableHead className="w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teams.map((team, index) => (
                    <TableRow key={team.id} className="border-slate-800">
                      <TableCell className="font-numbers">{index + 1}</TableCell>
                      <TableCell className="font-semibold">{team.name}</TableCell>
                      <TableCell className="text-center">
                        {team.group_id ? (
                          <span className="px-2 py-1 bg-primary/10 text-primary rounded text-sm font-semibold">
                            {getGroupName(team.group_id)}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              data-testid={`delete-team-${team.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-slate-900 border-slate-800">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Elimina squadra</AlertDialogTitle>
                              <AlertDialogDescription>
                                Sei sicuro di voler eliminare "{team.name}"? Questa azione eliminerà anche tutti i giocatori associati.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-slate-800 border-slate-700">Annulla</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDeleteTeam(team.id)}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                Elimina
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-slate-400">
                Nessuna squadra iscritta
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSquadrePage;
